import { readonly, ref } from '@nuxtjs/composition-api';

import { useApi } from '~/composables';
import setAddressesOnCart from '~/diptyqueTheme/customQueries/magento/setAddressesOnCartMutation';
import { Logger } from '~/helpers/logger';
import useCart from '~/modules/core/composables/useCart';
import type { Cart } from '~/modules/GraphQL/types';

import type { useAddressesErrors, useAddressesInterface, useAddressesSaveParams } from './useAddresses';

interface SetAddressesOnCartResponse {
  data: {
    setAddressesOnCart?: {
      cart: Cart;
    };
  };
}

/**
 * Allows saving addresses information for the same cart.
 *
 * See the {@link useAddressesInterface} for a list of methods and values available in this composable.
 */
export function useAddresses(): useAddressesInterface {
  const loading = ref(false);
  const error = ref<useAddressesErrors>({ save: null });
  const { cart, setCart } = useCart();
  const { mutate } = useApi();

  const save = async ({ addressesDetails }: useAddressesSaveParams): Promise<string | null> => {
    Logger.debug('useAddresses.setAddressesOnCart');
    let cardId: string | null = null;

    try {
      loading.value = true;

      const { id } = cart.value;

      const { email, shipping_address: shippingAddress, billing_address: billingAddress } = addressesDetails;

      const { data }: SetAddressesOnCartResponse = await mutate(setAddressesOnCart, {
        input: {
          cart_id: id,
          email: email,
          shipping_address: {
            ...shippingAddress
          },
          billing_address: {
            ...billingAddress
          }
        }
      });

      Logger.debug('[Result]:', { data });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (data?.setAddressesOnCart) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        cardId = data?.setAddressesOnCart?.cart?.id;

        // Set data to cart store to fix express checkout issue with addresses
        if (cardId) {
          setCart({ ...cart, ...data?.setAddressesOnCart?.cart });
        }
      }

      error.value.save = null;
    } catch (err) {
      error.value.save = err;
      Logger.error('useAddresses/setAddressesOnCart', err);
    } finally {
      loading.value = false;
    }

    return cardId;
  };

  return {
    save,
    error: readonly(error),
    loading: readonly(loading)
  };
}

export * from './useAddresses';
export default useAddresses;
